<template>
	<b-container class="login-container">
		<b-row v-show="errorText != null">
			<b-col cols="12" class="text-center mt-5">
				<b-alert show variant="danger" class="py-1">{{ errorText }}</b-alert>
			</b-col>
		</b-row>
		<b-row>
			<b-col class="d-flex justify-content-center">
				<b-form
					@submit.prevent="handleSubmit"
					class="col-12 col-sm-12 col-md-7 col-lg-6 col-xl-5"
				>
					<!-- user name -->
					<b-card bg-variant="light" class="mt-5">
						<b-card-title title-tag="h2" class="text-center text-secondary mb-4"
							>Welcome</b-card-title
						>

						<!-- user name -->
						<b-form-row>
							<b-col cols="12">
								<b-form-group
									label-for="username"
									:invalid-feedback="invalidFeedback('username')"
									:state="validateState('username')"
									class="login-form-input-group"
								>
									<b-input-group>
										<template v-slot:prepend>
											<b-input-group-text class="bg-white">
												<font-awesome-icon :icon="['fas', 'user-secret']" />
											</b-input-group-text>
										</template>
										<b-form-input
											autocomplete="username"
											id="username"
											type="text"
											name="username"
											ref="username"
											v-model.trim="form.username"
											v-validate="'required'"
											class="border-left-0 rounded-right"
										/>
									</b-input-group>
								</b-form-group>
							</b-col>
						</b-form-row>

						<!-- password -->
						<b-form-row class="mt-2">
							<b-col cols="12">
								<b-form-group
									label-for="password"
									:invalid-feedback="invalidFeedback('password')"
									:state="validateState('password')"
									class="login-form-input-group"
								>
									<b-input-group>
										<template v-slot:prepend>
											<b-input-group-text class="bg-white">
												<font-awesome-icon :icon="['fas', 'lock']" />
											</b-input-group-text>
										</template>
										<b-form-input
											autocomplete="current-password"
											id="password"
											type="password"
											name="password"
											ref="password"
											v-model.trim="form.password"
											v-validate="'required'"
											class="border-left-0 rounded-right"
										/>
									</b-input-group>
								</b-form-group>
							</b-col>
						</b-form-row>

						<!-- action button -->
						<b-form-group class="mt-4">
							<b-row>
								<b-col cols="12">
									<b-button
										type="submit"
										:disabled="$wait.is('login in') || !isFormValid"
										variant="primary"
										block
										size="lg"
									>
										<v-wait for="login in">
											<template slot="waiting">
												<font-awesome-icon
													spin
													:icon="['fas', 'circle-notch']"
												/>&nbsp;Logging...
											</template>
											Sign In
										</v-wait>
									</b-button>
								</b-col>
							</b-row>
							<b-form-row>
								<b-col>
									<p
										v-show="recaptchaInvalidMessage"
										class="text-center text-danger m-0 p-0"
									>
										<small>{{ recaptchaInvalidMessage }}</small>
									</p>
								</b-col>
							</b-form-row>
						</b-form-group>
						<vue-recaptcha
							ref="recaptcha"
							@verify="onCaptchaVerified"
							@expired="resetCaptcha"
							size="invisible"
							:sitekey="recaptchaSiteKey"
							:loadRecaptchaScript="true"
						></vue-recaptcha>
					</b-card>
				</b-form>
			</b-col>
		</b-row>
	</b-container>
</template>

<script>
	import { mapState, mapActions } from "vuex";
	import VueRecaptcha from "vue-recaptcha";
	import { isNull } from "lodash";

	export default {
		name: "login_view",
		components: { VueRecaptcha },
		data() {
			return {
				form: {
					username: null,
					password: null,
				},
				errorText: null,
				recaptchaInvalidMessage: false,
				recaptchaSiteKey: process.env.VUE_APP_GOOGLE_RECAPTCHA_KEY,
			};
		},
		computed: {
			...mapState("account", ["status"]),

			isFormValid() {
				return (
					!this.veeErrors.any() &&
					!isNull(this.form.username) &&
					!isNull(this.form.password)
				);
			},
		},
		methods: {
			...mapActions("account", ["LOGIN"]),

			// submit button clicked
			handleSubmit() {
				this.$validator.validateAll().then((result) => {
					// if any error, return, do not submit form
					if (!result) {
						return;
					}

					this.$refs.recaptcha.execute();
				});
			},

			// send closure-pass to backend to verify access
			onCaptchaVerified(recaptchaToken) {
				this.$wait.start("login in");

				const { username, password } = this.form;

				if (username && password) {
					this.$store
						.dispatch("account/LOGIN", {
							username: username,
							password: password,
							"g-recaptcha-response": recaptchaToken,
						})
						.then(
							() => {
								this.$wait.end("login in");
								this.errorText = null;
							},
							(error) => {
								this.$wait.end("login in");
								this.errorText = error.message;
								// reset recaptcha
								this.$nextTick(() => {
									this.resetCaptcha();
								});
							}
						);
				}
			},

			// reset recaptcha
			resetCaptcha() {
				this.$refs.recaptcha.reset();
			},

			// get the state of the field (valid = true || false)
			validateState(ref) {
				if (
					this.veeFields[ref] &&
					(this.veeFields[ref].dirty || this.veeFields[ref].validated)
				) {
					return !this.veeErrors.has(ref);
				}
				return null;
			},

			invalidFeedback(ref) {
				const messages = {
					username: "enter your G2K username",
					password: "enter your G2K password",
				};

				return messages[ref];
			},
		},
	};
</script>
<style>
	.login-form-input-group.is-invalid .invalid-feedback {
		text-align: right;
		bottom: 0;
		position: absolute;
		right: 5px;
	}

	/* Change Autocomplete styles in Chrome*/
	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	textarea:-webkit-autofill,
	textarea:-webkit-autofill:hover,
	textarea:-webkit-autofill:focus,
	select:-webkit-autofill,
	select:-webkit-autofill:hover,
	select:-webkit-autofill:focus {
		-webkit-text-fill-color: green;
		-webkit-box-shadow: 0 0 0px 1000px white inset;
		box-shadow: 0 0 0px 1000px white inset;
		transition: background-color 5000s ease-in-out 0s;
	}
</style>
